/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

/**
 * Utility pro práci s HTTP požadavky ve Fetch API
 *
 * Zdroje:
 * - [Request WHATWG](https://fetch.spec.whatwg.org/#request-class)
 * - [Request MDN](https://developer.mozilla.org/en-US/docs/Web/API/Request)
 */
class RequestUtil {
  /**
   * @param {HeadersUtil} headersUtil
   */
  constructor(headersUtil) {
    this.headersUtil = headersUtil;
  }

  /**
   * Sloučí několik inicializačních objektů požadavků do jednoho.
   * Funguje to obdobně jako `Object.assign`, s vyjímkou položky `headers`.
   * Tato položka se nepřepisuje jako celek, místo toho jsou jednotilvé hlavičky
   * sloučeny dle svých názvů, opět dle algoritmu podobnému k `Object.assign`.
   * Narozdíl od `Object.assign` je metoda imutabilní (chybí `target`)
   * a není podporováno `undefined` a `null` na vstupu.
   *
   * Zdroje:
   * -[Object.assign MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
   * @param {...RequestInit} sources
   * @returns {RequestInit}
   */
  mergeInit(...sources) {
    // Prochází se všechny zdroje a výsledek se ukládá do cílového objektu.
    return sources.reduce((acc, source) => {
      // Properties in the target object are overwritten by properties
      // in the source if they have the same key.
      Object.entries(source).forEach(([key, value]) => {
        // Klíč `headers` se slučuje podle vlastního algoritmu.
        if (key === 'headers') {
          // Pokud nejsou hlavičky definovány, tak se nic nedělá.
          if (isNil(value)) {
            return;
          }
          // Pokud nejsou definovány hlavičky v akumulátoru,
          // použijí se ty ze zdroje, po tom co se normalizují.
          const { headersUtil } = this;
          const accHeaders = acc.headers;
          if (isNil(accHeaders)) {
            acc.headers = headersUtil.mergeInit(value);
            return;
          }
          // Pokud jsou definovány hlavičky v akumulátoru i ve zdroj,
          // tak se sloučí.
          acc.headers = headersUtil.mergeInit(acc.headers, value);
          return;
        }
        // Ostatní položky ze source přímo přepisují hodnoty
        // v cílovém objektu.
        acc[key] = value;
      });
      return acc;
    }, {});
  }

  /**
   * Převede požadavek zpět na inicializační objekt, který ho vytvořil.
   * Jednotlivé položky ve výsledném inicializačním objektu jsou vždy přítomné,
   * aby se předešlo problému s rozdílnými výchozími hodnotami mezi jednotlivými
   * verzemi specifikace Fetch API.
   * @param {Request} request
   * @returns {Promise.<RequestInit>}
   */
  toInit(request) {
    return new Promise((resolve) =>
      resolve(
        request.text().then((text) => ({
          // Nejde rozlišit jestli byla v RequestInit hodnota "" nebo undefined,
          // ale "" nelze použít při GET a HEAD, takže se použije undefined.
          // Text je vždy string, takže u operátoru || je falsy pouze "".
          body: text || undefined,
          cache: request.cache,
          credentials: request.credentials,
          headers: this.headersUtil.toObject(request.headers),
          integrity: request.integrity,
          keepalive: request.keepalive,
          method: request.method,
          mode: request.mode,
          redirect: request.redirect,
          referrer: request.referrer,
          referrerPolicy: request.referrerPolicy,
          signal: request.signal,
          window: request.window
        }))
      )
    );
  }
}

export default RequestUtil;
